import { useState, useRef } from 'react';
import { useLocation } from 'wouter';
import { api } from '../lib/api';
import { Upload as UploadIcon, FileText, ArrowLeft, Check } from 'lucide-react';
import BottomNav from '../components/BottomNav';
import { extractTextFromPDF } from '../utils/pdf';

export default function Upload() {
  const [, setLocation] = useLocation();
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [mode, setMode] = useState<'quick' | 'standard' | 'deep'>('standard');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      if (!title) {
        setTitle(selectedFile.name.replace('.pdf', ''));
      }
    }
  };

  const handleUpload = async () => {
    if (!file || !title) return;

    setUploading(true);
    setProgress(10);

    try {
      // 1. 提取PDF文本
      setProgress(20);
      const { text, totalPages } = await extractTextFromPDF(file);
      
      // 2. 上传PDF文件
      setProgress(40);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title);
      formData.append('author', author);
      formData.append('totalPages', totalPages.toString());
      formData.append('generationMode', mode);
      
      const uploadResponse: any = await api.uploadBook(formData);
      
      if (!uploadResponse.success) {
        throw new Error(uploadResponse.error || '上传失败');
      }
      
      const bookId = uploadResponse.data.id;
      
      // 3. 触发AI生成卡片
      setProgress(60);
      const generateResponse = await api.generateCards(bookId, text);
      
      if (!generateResponse.success) {
        throw new Error(generateResponse.error || '生成卡片失败');
      }
      
      // 4. 完成并跳转
      setProgress(100);
      setTimeout(() => {
        setLocation('/books');
      }, 1000);
    } catch (error) {
      console.error('Upload failed:', error);
      alert('上传失败,请重试');
      setUploading(false);
    }
  };

  const modeConfig = {
    quick: { count: 15, time: '5-10分钟' },
    standard: { count: 60, time: '15-20分钟' },
    deep: { count: 120, time: '30-40分钟' }
  };

  return (
    <div className="min-h-screen gradient-bg pb-24">
      {/* Header */}
      <header className="glass-strong border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-screen-sm mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => setLocation('/home')}
            className="w-10 h-10 glass rounded-xl flex items-center justify-center hover:bg-bg-secondary transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-xl font-bold">上传书籍</h1>
        </div>
      </header>

      <div className="max-w-screen-sm mx-auto px-6 py-8 space-y-6">
        {/* Upload Area */}
        <div
          onClick={() => !uploading && fileInputRef.current?.click()}
          className={`glass-strong rounded-2xl p-8 border-2 border-dashed border-primary/30 hover:border-primary/60 transition-colors cursor-pointer ${
            uploading ? 'opacity-50 pointer-events-none' : ''
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-gradient-accent rounded-2xl flex items-center justify-center shadow-glow">
              {file ? (
                <FileText size={32} className="text-white" />
              ) : (
                <UploadIcon size={32} className="text-white" />
              )}
            </div>
            
            {file ? (
              <>
                <h3 className="font-semibold text-lg">{file.name}</h3>
                <p className="text-sm text-text-secondary">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </>
            ) : (
              <>
                <h3 className="font-semibold text-lg">选择文件 (.pdf)</h3>
                <p className="text-sm text-text-secondary">
                  支持最大 50MB 的 PDF 文件
                </p>
              </>
            )}
          </div>
        </div>

        {/* Book Info */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2 text-text-secondary">
              书籍名称 *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="请输入书籍名称"
              className="w-full px-4 py-3 bg-bg-secondary rounded-xl text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={uploading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-text-secondary">
              作者
            </label>
            <input
              type="text"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              placeholder="请输入作者名称"
              className="w-full px-4 py-3 bg-bg-secondary rounded-xl text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={uploading}
            />
          </div>
        </div>

        {/* Generation Mode */}
        <div>
          <label className="block text-sm font-medium mb-3 text-text-secondary">
            生成模式
          </label>
          <div className="grid grid-cols-3 gap-3">
            {(['quick', 'standard', 'deep'] as const).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                disabled={uploading}
                className={`glass-strong rounded-xl p-4 transition-all ${
                  mode === m
                    ? 'ring-2 ring-primary bg-primary/10'
                    : 'hover:bg-bg-secondary'
                } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold">
                    {m === 'quick' ? '快速' : m === 'standard' ? '标准' : '深度'}
                  </span>
                  {mode === m && (
                    <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                      <Check size={14} className="text-white" />
                    </div>
                  )}
                </div>
                <p className="text-xs text-text-secondary mb-1">
                  {modeConfig[m].count}张卡片
                </p>
                <p className="text-xs text-text-secondary">
                  约{modeConfig[m].time}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Upload Button */}
        <button
          onClick={handleUpload}
          disabled={!file || !title || uploading}
          className="w-full py-4 bg-gradient-accent rounded-2xl text-white font-medium shadow-glow hover:scale-105 active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
        >
          {uploading ? '生成中...' : '开始生成'}
        </button>

        {/* Progress */}
        {uploading && (
          <div className="glass-strong rounded-2xl p-6 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-secondary">生成进度</span>
              <span className="text-primary font-semibold">{progress}%</span>
            </div>
            <div className="h-2 bg-bg-primary rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-accent transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-sm text-text-secondary text-center">
              正在生成知识卡片,请稍候...
            </p>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
